import { Fragment, useEffect, useMemo, useState } from 'react'
import { myLanes } from '../lib/support'
import type { Me } from '../lib/me'
import { can, canEverywhere, canGallery } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { supabase } from '../lib/supabase'
import {
  loadFeed,
  loadMyBylines,
  loadMyPostToEdit,
  loadTrendingTags,
  loadWikiAnswers,
  placeRecognitionGroups,
  relativeTime,
  shapeFeed,
  type FeedItem,
  type MyByline,
  type TrendingTag,
  type UnpublishedPost,
  type WikiItem,
} from '../lib/feed'
import { ANNOUNCEMENT_VIDEO, VIDEO_RELEASED } from '../lib/video'
import { AppShell } from '../components/AppShell'
import { Hero } from '../components/Hero'
import { Card } from '../components/Card'
import { ContestResults } from '../components/ContestResults'
import { FeedPost } from '../components/FeedPost'
import { CountdownCard } from '../components/CountdownCard'
import { OneQuestionCard } from '../components/OneQuestionCard'
import { loadOneQuestion, type OneQuestionHub } from '../lib/oneQuestion'
import { ContestCard } from '../components/ContestCard'
import { hubContest, loadContestList, type Contest } from '../lib/photoContests'
import { PinnedAnnouncement } from '../components/PinnedAnnouncement'
import { VideoSchedule } from '../components/VideoSchedule'
import { VideoViews } from '../components/VideoViews'
import { loadVideoGate, videoCardVisible, type VideoGate } from '../lib/videoGate'
import { Composer } from '../components/Composer'
import { PostFab } from '../components/PostFab'
import { DraftPicker } from '../components/DraftPicker'
import { Dialog } from '../components/Dialog'
import { MyCardWidget } from '../components/BusinessCard'
import { WikiAnswers } from '../components/WikiAnswers'
import { TrendingTags } from '../components/TrendingTags'
import { StarsGivenWidget, StarsReceivedWidget } from '../components/StarOneWidget'
import { StarOneAgreement } from '../components/StarOneAgreement'
import { useStarOne } from '../lib/terms'
import {
  loadMyStars, loadStarSeries, loadFeaturedEndorsements, loadFeedRecognitions,
  type Endorsement, type MyStars, type RecognitionGroup, type StarBucket,
} from '../lib/stars'
import { FeedRecognitions } from '../components/FeedRecognitions'
import { Notices } from '../components/Notices'
import { Thread, POST_LABELS } from '../components/Thread'
import { HUB_NOTICES } from '../lib/hubNotices'
import { loadCountdown, type LaunchCountdown } from '../lib/launch'
import { loadCheckNotes, postNotes, type CheckNote } from '../lib/moderation'
import { PostModerationBar } from '../components/moderation/PostModerationBar'
import type { ModeOption } from '../components/IdentityMenu'
import { useHubLang } from '../lib/i18n'
import { onStarsEarned } from '../lib/starPulse'
import { HOME, HERO, STARS } from '../lib/hubCopy'
import { GiveForm } from '../components/GiveRecognition'

/**
 * Roles that can publish. Drives the EMPLOYEE-VIEW TOGGLE, nothing else.
 *
 * Deliberately still a role list and not a permission check: the toggle asks
 * "is this person staff-facing comms, who would want to preview what an
 * ordinary employee sees", which is a question about their job rather than
 * about a capability. The composer below asks a capability question and uses
 * can() instead. Do not merge the two — 015 gave every employee content.post,
 * so a permission check here would put the preview toggle in front of ~900
 * people who have nothing to preview.
 */
const PUBLISHER_ROLES = new Set(['comms_admin', 'tenant_admin'])

type Content =
  | { kind: 'loading' }
  | { kind: 'ready'; posts: FeedItem[]; wiki: WikiItem[] }
  | { kind: 'error'; message: string }

/**
 * The engagement hub.
 *
 * ── WHAT IS REAL, AS OF 30 Aug ──────────────────────────────────────────────
 *
 * The person, their tenant and roles (loadMe, under RLS); the feed, the pinned
 * announcement and Wiki One (loadFeed / loadWikiAnswers, through the migration
 * 012 RPCs, also under RLS); the announcement video, which is a real private
 * Vimeo embed. The category filter and the employee-view toggle are local
 * state.
 *
 * ── AND AS OF 20 Sep THERE IS NOTHING INVENTED ON THIS PAGE AT ALL ─────────
 *
 * `lib/placeholder.ts` is DELETED. The last three values in it went together:
 * the two-cell countdown and the days-to-preview line in the strip are now one
 * card reading `launch_countdown()`, and the unread badge is gone rather than
 * fixed — the bell already counts notifications and two surfaces counting one
 * thing is how a badge starts lying.
 *
 * The rebrand rollout counters were real (230 assets through
 * `rebrand_progress()`) and are gone too, deliberately: David retired CMP-2 on
 * 20 Sep, widget and registry together. Migration 094 part D.
 *
 * So the placeholder NOTICE narrows with them — see lib/hubNotices.tsx. A
 * notice that over-claims trains people to ignore it, which is the one thing
 * it cannot afford to do.
 *
 * ── THE PINNED POST IS TAKEN FROM THE FEED, NOT FETCHED SEPARATELY ──────────
 *
 * `content.is_pinned` is the flag, feed_page() already orders pinned first, and
 * this route splits the first pinned row out into the hero slot. One query, one
 * definition of pinned. A second fetch would be a second definition, and the
 * two would disagree the first time someone pinned two things.
 *
 * The VIDEO, though, is attached to that slot rather than to the row — there is
 * exactly one announcement video and no attachment model until FND-6. See
 * lib/video.ts. So pinning a different post would move the video with the slot,
 * which is wrong, and is the reason FND-6 is not optional for long.
 *
 * Since 5 Oct the card renders with NO pinned post too — the video alone, under
 * its own title — because prod has never had one and the launch video was
 * otherwise on no screen. See PinnedAnnouncement.
 */
export function Home({ me }: { me: Me }) {
  // Rendered BELOW App's HubLangRoot, so the route itself may read it.
  const lang = useHubLang()
  const h = HOME[lang]
  // The banner's words, English from lib/copy.ts and Spanish beside it (SHL-25).
  const hero = HERO[lang]
  const [employeeView, setEmployeeView] = useState(false)
  const [content, setContent] = useState<Content>({ kind: 'loading' })
  // Loaded separately from the feed, and its failure is deliberately NOT the
  // feed's failure: a countdown that will not load is a missing card, while a
  // feed that will not load is a broken hub. Folding them into one Promise.all
  // would take the whole page down over a number in the rail.
  const [countdown, setCountdown] = useState<LaunchCountdown | null>(null)
  // One Question (105). Its own state and its own catch, the countdown's
  // posture: a question that will not load is a missing card, never a broken
  // hub. The version bumps after an answer so the card re-reads the server.
  const [oneQuestion, setOneQuestion] = useState<OneQuestionHub | null>(null)
  const [oneQuestionVersion, setOneQuestionVersion] = useState(0)
  // 147 (CMP-6). The one contest the server puts on the hub, or none.
  const [contest, setContest] = useState<Contest | null>(null)
  /* 156. null until read, and on a failed read: the card waits (fails closed). */
  const [videoGate, setVideoGate] = useState<VideoGate | null>(null)
  const [videoGateVersion, setVideoGateVersion] = useState(0)

  /**
   * The post dialog and the drafts dialog. FED-35.
   *
   * Mounted HERE, by the route, and not inside the feed card — FED-35 says so
   * and 059's rule is why: "Post something" has to mean the same thing
   * everywhere, and a second copy of a create form is how a second copy of the
   * thing gets published. One <Composer> on this page, two ways in.
   *
   * `editing` is the draft carried into the composer, or null for a blank
   * post. It is NOT a third boolean: "the post dialog is open" and "it is open
   * on a draft" are one piece of state, and splitting them is how the two
   * launchers problem (HLP-15) starts.
   */
  const [posting, setPosting] = useState(false)
  const [editing, setEditing] = useState<UnpublishedPost | null>(null)
  const [pickingDraft, setPickingDraft] = useState(false)
  /* 115 (FED-39). Whether `editing` is one of your LIVE posts rather than a
     saved draft. A property OF the edit, set and cleared in the same commits
     as `editing` — not a third way to open the dialog — and it is what sends
     the save through the check before the words change. */
  const [editingLive, setEditingLive] = useState(false)
  const [editLoadFailed, setEditLoadFailed] = useState(false)
  /* 300. The bylines this person may post under — empty for nearly everyone.
     A failed read is the same as none: the composer then offers yourself. */
  const [myBylines, setMyBylines] = useState<MyByline[]>([])

  /* Load YOUR post the way the composer needs it (source-language words,
     photo sizes) and open the one composer on it. Nothing opens on a miss:
     a composer prefilled from the feed card would carry a translation or
     drop a photo's size, and the save would then lose it (094 E). */
  async function openLiveEdit(contentId: string) {
    setEditLoadFailed(false)
    try {
      const post = await loadMyPostToEdit(contentId)
      if (!post) {
        setEditLoadFailed(true)
        return
      }
      setUnchecked(false)
      setEditing(post)
      setEditingLive(true)
      setPosting(true)
    } catch {
      setEditLoadFailed(true)
    }
  }

  /* An unchecked post used to say so inside the collapsed composer. There is
     no collapsed composer now, and the dialog closes on success, so the notice
     has to outlive the surface that produced it or the author finds out from
     the feed — which is the one thing the composer's own header says must not
     happen. */
  const [unchecked, setUnchecked] = useState(false)
  const [trending, setTrending] = useState<TrendingTag[]>([])
  const [stars, setStars] = useState<MyStars | null>(null)
  const [starSeries, setStarSeries] = useState<StarBucket[]>([])
  // The give dialog (25 Sep — see StarOneWidget's header). The featured
  // endorsements are read when it first opens, not on every hub load: most
  // visits recognise nobody.
  const [giving, setGiving] = useState(false)
  const [giveFeatured, setGiveFeatured] = useState<Endorsement[] | null>(null)
  // 333. The feed's Star One groups, by gap. See the note above the feed.
  const [recGroups, setRecGroups] = useState<Map<number, RecognitionGroup>>(new Map())
  const [recVersion, setRecVersion] = useState(0)

  // 165. Whether the reader has joined Star One (TermsGate provides it).
  const starOne = useStarOne()

  function openGive() {
    setGiving(true)
    if (giveFeatured === null) {
      // No presets is still a working form (128): the person types their own.
      loadFeaturedEndorsements().then(setGiveFeatured).catch(() => setGiveFeatured([]))
    }
  }

  // After a give, re-read what the two cards show — Star One's afterGive, for
  // the same reason (028c): the screen shows what the database holds.
  async function afterGive() {
    setStars(await loadMyStars())
    // 333: the new recognition belongs in the feed's newest group.
    setRecVersion((v) => v + 1)
  }

  const tenantName = me.tenants[0]?.name ?? 'No tenant resolved'
  const roleName = me.roles[0]?.name ?? 'No role'
  const canPublish = me.roles.some((r) => PUBLISHER_ROLES.has(r.key))

  // The composer's gate, and note it asks a different question. content.post
  // is the permission 015 gave every employee; employeeView hides the composer
  // for the same reason it hides the diagnostics card — presentation only,
  // never a permission. RLS is the gate, and it was tested, not assumed.
  const canPost = can(me, 'content.post') && !employeeView

  useEffect(() => {
    if (!canPost) return
    let live = true
    loadMyBylines(me.locale)
      .then((b) => live && setMyBylines(b))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [canPost, me.locale])

  // One "start a new post", for the bar above the feed AND the floating
  // button — two doors, one composer (FED-35).
  function startPost() {
    setEditing(null)
    setEditingLive(false)
    setPosting(true)
  }


  // Whether the Administration section appears in the nav. Presentation only:
  // every function behind it is SECURITY DEFINER and checks admin.roles itself,
  // which is the actual boundary. `employeeView` hides it so the preview toggle
  // shows what an ordinary colleague sees.
  const canAdminRoles = can(me, 'admin.roles') && !employeeView

  // Same shape for the Review section. 325: the queue's COUNT is no longer
  // fetched here - the shell reads every waiting count in one call
  // (lib/waiting.ts), and only the Moderation route still passes its own.
  const canModerate = can(me, 'content.moderate') && !employeeView

  // Bumped after a successful post, which re-runs the feed effect. A refetch
  // rather than splicing the new post into local state: feed_page decides
  // order, audience and locale resolution, and a client that inserted the row
  // itself would be guessing at all three — and would get the ordering wrong
  // the moment anything is promoted.
  const [feedVersion, setFeedVersion] = useState(0)

  /**
   * Moderator mode — FED-7. Off on every load, deliberately.
   *
   * Arming does two things: it asks feed_page for hidden posts, and it renders
   * the controls on each card. It is NOT a permission — passing
   * p_include_hidden=true grants nothing, RLS still decides, and an employee
   * who somehow flipped this boolean would see exactly the feed they saw
   * before. That separation is why it is safe for an authorisation-shaped
   * question to be answered by a boolean living in the browser.
   *
   * It resets on reload because remove is irreversible: a mode that persists
   * is a mode somebody forgets they are in.
   */
  const [moderatorMode, setModeratorMode] = useState(false)

  /* Notice authoring LEFT THE HUB on 11 Oct (SHL-33, David 10 Oct: "they
     don't need to be in a view on hub"). It was a "Manage notices" mode here,
     lifted from <Notices> on 12 Sep, on 039's argument that you edit what
     everybody sees while looking at it. That benefit now lives on
     #/admin/notices, whose top is the live strip as a preview — see
     routes/AdminNotices.tsx. Do not bring the mode back on 039's strength. */

  /**
   * Every mode this screen offers, in one list, for the topbar Options menu.
   *
   * ── EMPLOYEE VIEW DISARMS THE OTHER TWO, AND MUST ───────────────────────────
   *
   * `canModerate` is already `&& !employeeView`, so a
   * mode armed before switching to employee view would be armed, invisible and
   * still filtering the feed — the preview would be a preview of neither view.
   * The list is rebuilt from those same flags, so the row disappears with the
   * controls it drives, and `moderatorMode` is reset below rather than left
   * silently true.
   *
   * The list is empty for anybody with no second role, and the identity menu
   * renders no modes section at all for an empty list. That is the ask: the
   * same hub for the ~900 until somebody goes looking for their other job.
   * (The menu itself still renders — everybody has a name and a way out. Only
   * the modes block is conditional. SHL-19.)
   */
  const modes: ModeOption[] = [
    ...(canPublish
      ? [
          {
            key: 'employee',
            label: 'Employee view',
            tone: 'preview' as const,
            on: employeeView,
            detail:
              'See the page as somebody with no publishing rights sees it. Turns the other modes off.',
            onToggle: (next: boolean) => {
              setEmployeeView(next)
              if (next) setModeratorMode(false)
            },
          },
        ]
      : []),
    ...(canModerate
      ? [
          {
            key: 'moderator',
            label: 'Moderator mode',
            // `danger`, not `preview`, and the distinction is ModeSwitch's
            // rule: arming this puts an irreversible Remove on every card.
            tone: 'danger' as const,
            on: moderatorMode,
            detail: 'Show hidden posts, and moderator controls on every card.',
            onToggle: setModeratorMode,
          },
        ]
      : []),
  ]

  useEffect(() => {
    let live = true
    // No locale argument: feed_page() defaults to this person's own
    // person.locale server-side, which is one fewer place for the client to
    // disagree with the database about who the viewer is.
    Promise.all([loadFeed(undefined, moderatorMode && canModerate), loadWikiAnswers()])
      .then(([posts, wiki]) => live && setContent({ kind: 'ready', posts, wiki }))
      .catch((e: Error) => live && setContent({ kind: 'error', message: e.message }))
    return () => {
      live = false
    }
  }, [feedVersion, moderatorMode, canModerate])

  /* 127 (FED-25). What the check said about each post on the page — only in
     moderator mode, and only after the feed has loaded, so the page is never
     waiting on it. One call for the page (check_notes takes the ids), never
     one per card. A failure leaves the notes out rather than the feed. */
  const [checkNotes, setCheckNotes] = useState<Map<string, CheckNote> | null>(null)
  const reviewing = moderatorMode && canModerate
  const feedIds = content.kind === 'ready' ? content.posts.map((p) => p.contentId).join(',') : ''
  useEffect(() => {
    let live = true
    if (!reviewing || !feedIds) {
      setCheckNotes(null)
      return
    }
    loadCheckNotes(feedIds.split(','))
      .then((notes) => live && setCheckNotes(postNotes(notes)))
      .catch(() => live && setCheckNotes(null))
    return () => {
      live = false
    }
  }, [reviewing, feedIds])

  /* Its own effect, and deliberately NOT folded into the Promise.all above.
     Trending is a side-rail glance; the feed is the page. Putting it in that
     pair would let a slow or failing tag count hold up the thing people came
     for, and its catch sets an empty list rather than an error — the card then
     renders nothing at all, which is the right amount of noise for a widget
     nobody is waiting on. It re-reads with feedVersion so posting a tagged
     reply moves the number without a reload. */
  useEffect(() => {
    let live = true
    loadTrendingTags()
      .then((t) => live && setTrending(t))
      .catch(() => live && setTrending([]))
    return () => {
      live = false
    }
  }, [feedVersion])

  useEffect(() => {
    let live = true
    loadCountdown()
      .then((c) => live && setCountdown(c))
      // A missing card, not a broken page. `launch_countdown()` REFUSES when
      // comms have set no date (rule 12), so this catch is the ordinary path
      // for a tenant nobody has configured yet — and null renders nothing
      // rather than "0 days".
      .catch(() => live && setCountdown(null))
    return () => {
      live = false
    }
  }, [])

  useEffect(() => {
    let live = true
    loadOneQuestion()
      .then((q) => live && setOneQuestion(q))
      .catch(() => live && setOneQuestion(null))
    return () => {
      live = false
    }
  }, [oneQuestionVersion])

  /* 156 (David, 11 Oct). When the video card is shown to staff. Fails CLOSED
     — see lib/videoGate.ts: an early video is the failure being prevented. */
  useEffect(() => {
    let live = true
    loadVideoGate()
      .then((g) => live && setVideoGate(g))
      .catch(() => live && setVideoGate(null))
    return () => {
      live = false
    }
  }, [videoGateVersion])

  /* 147. Read once on load; a failure shows no card rather than an error —
     the contest is an invitation, and the feed below is what the hub is for. */
  useEffect(() => {
    let live = true
    loadContestList()
      .then((list) => live && setContest(hubContest(list)))
      .catch(() => live && setContest(null))
    return () => {
      live = false
    }
  }, [])

  /* REC-8. Star One's summary, read once on load.
     Two reads rather than one because they answer different questions and only
     one of them is about the reader: `my_stars()` is self-only by design, the
     series is the chart. A failure on either leaves the card showing a dash
     or no chart rather than taking the hub down with it — the same
     posture as the rebrand board above. */
  useEffect(() => {
    let live = true
    loadMyStars()
      .then((s) => live && setStars(s))
      .catch(() => live && setStars(null))
    // The received card's chart. Its last-three list left the rail on 11 Oct
    // (David: hooks, not lists), and so did the given card's — which is why
    // neither my_star_earnings nor recognition_wall is read here any more.
    // Both lists are one tap away: the earnings dialog and #/stars.
    const loadEarned = () => {
      loadStarSeries('12m')
        .then((b) => live && setStarSeries(b))
        .catch(() => live && setStarSeries([]))
    }
    loadEarned()
    // When the "+25 stars" pill fires, this card is the number it is talking
    // about — so it re-reads rather than sitting a comment behind the pill.
    // The re-read of my_stars() sees the same balance the pill just did, so
    // it cannot pop a second time.
    const off = onStarsEarned(() => {
      loadMyStars().then((s) => live && setStars(s)).catch(() => {})
      loadEarned()
    })
    return () => {
      live = false
      off()
    }
  }, [])

  /* RECOGNITIONS ON THE FEED — YES, AS ONE CARD PER GAP (333, David, 30 Oct).

     Until today the answer was "none", for one reason: ~932 people giving
     stars would bury the handful of posts the feed exists for. A platform-
     support idea (29 Oct) said the cost of that: "unless I intentionally click
     to go into Star One, I don't see all the cool things people are saying
     about each other." Prod that day: 34 recognitions to 18 posts in a week.

     So recognitions given between two posts are ONE Star One card in that
     gap (FeedRecognitions): the newest three, Show all for the rest. The
     posts stay the feed's spine — feed_page is untouched and its LIMIT still
     counts posts only, so recognitions can never push a post off the page.
     The groups come from feed_recognition_groups, which reads the wall itself
     (one definition of a visible recognition) and counts in the database.

     STILL TRUE, and why there is no scope switch: do not add a control that
     filters or re-sorts people's recognitions on the feed without reading D10
     and B22 — that is one short step from the leaderboard David has declined
     three times. Newest first, as the wall. Do not unroll a group into one
     card per recognition either; the grouping IS the answer to the volume. */

  const posts = content.kind === 'ready' ? content.posts : []

  /* The pinned split lives in lib/feed.ts, not here — it is the only
     arithmetic on this page that can be wrong while still compiling and still
     rendering, so it is unit-tested. See test/hub-render.test.tsx.

     `counts` and `filterByAudience` are NOT called any more, and both are kept
     in lib/feed.ts with their assertions rather than deleted: they are correct,
     and FED-37 is the issue for what replaces the audience chips. A tested
     function with no caller is cheap; re-deriving one under time pressure is
     not. */
  const { pinned, rest } = useMemo(() => shapeFeed(posts), [posts])
  const visible = rest
  // 333. Which Star One group goes before which post (lib/feed.ts, tested).
  const placement = useMemo(() => placeRecognitionGroups(rest), [rest])
  const boundsKey = placement.bounds.join('|')

  // Read after the feed, and again whenever it re-reads (feedVersion moves
  // `rest`) or a recognition is given here. A failure leaves the feed as it
  // was before 333: posts only, no error over the hub.
  useEffect(() => {
    let live = true
    if (placement.bounds.length === 0) {
      setRecGroups(new Map())
      return
    }
    loadFeedRecognitions(placement.bounds)
      .then((g) => live && setRecGroups(g))
      .catch((e) => {
        // Quiet on screen, never silent: 333 went live timing out on every
        // call and nothing anywhere said so (334). The console line is the
        // first place to look when the cards are missing.
        console.warn('feed_recognition_groups failed:', e?.message ?? e)
        if (live) setRecGroups(new Map())
      })
    return () => {
      live = false
    }
    // boundsKey stands for placement.bounds: same times, same read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundsKey, recVersion])

  return (
    <AppShell
      current="Engagement Hub"
      canAdmin={canAdminRoles}
      canShop={canReachShopAdmin(me)}
      canModerate={canModerate}
      // Hidden in the employee preview, like People and roles and Moderation.
      canStarsAdmin={can(me, 'stars.admin') && !employeeView}
      canPhotoRoom={canGallery(me) && !employeeView}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={employeeView ? 'Employee (view)' : roleName}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
      modes={modes}
    >
      <Notices notices={HUB_NOTICES} me={me} />

      {/* THE STATUS STRIP IS GONE. David, 20 Sep.

          It was a thin line under the topbar reading "N posts · N answers in
          Wiki One", and until earlier today a third figure counting down to
          the management preview. The countdown left because there is one
          countdown on this page and it is in the rail; the other two left
          because they are counts of things that are RIGHT THERE. The feed says
          how many posts it has in its own header, and the Wiki One card says
          how many answers it is showing. A strip restating both is a third
          surface for two numbers that already have one, which is how a badge
          starts lying (SHL-8's argument, one screen earlier).

          The whole component is deleted. Do not reintroduce it as a home for
          the next thing that needs a shelf — that is exactly how it ended up
          carrying the mode pills in the first place. */}

      <Hero
        eyebrow={`${tenantName} · ${hero.launchDate}`}
        headline={hero.headline}
        subhead={hero.subhead}
      />

      {/* "N updates since you last logged in" used to sit here, on an invented
          count with no table behind it (SHL-3, FND-5). It is gone rather than
          fixed: what it was reaching for is the BELL, which is real, reads
          `notification`, and is already in the topbar. Two surfaces counting
          one thing is how a badge starts lying — the argument SHL-8 makes
          about a dashboard panel, applied one screen earlier. David, 20 Sep. */}

      <div className="hub__cols">
        <div className="hub__main">
          {/* ONE QUESTION AND THE CONTEST LEFT THIS COLUMN on 11 Oct (David:
              "keep the main feed clean"). They are the first two hooks in
              the rail below, which is the swipe row above the feed on a
              phone — so they are still the first thing a phone shows. */}

          {/* ALWAYS RENDERED since 5 Oct (David). It used to wait for a
              pinned post, prod has none and nothing can pin one, so the
              launch video was on no screen and the welcome modal's "Watch
              the announcement" card pointed here at nothing. With a pinned
              post it is that post's card; without, it is the video alone.
              HELD 6-10 Oct (David); released 10 Oct with the final cut. VIDEO_RELEASED
              — see lib/video.ts for what that also withholds.
              156 (11 Oct): and a show-from TIME, data not code, so the card
              waits for Jamie's announcement. Schedulers see it early, under
              the strip that says so; everybody else sees nothing until then. */}
          {VIDEO_RELEASED && videoGate?.canSchedule && (
            <VideoSchedule
              gate={videoGate}
              locale={me.locale}
              onChanged={() => setVideoGateVersion((v) => v + 1)}
            />
          )}
          {/* 198 (14 Oct): view counts, totals only, for whole-company
              analytics.read. The server refuses anyone else (rule 12). */}
          {VIDEO_RELEASED && videoCardVisible(videoGate) && canEverywhere(me, 'analytics.read') && (
            <VideoViews videoId={ANNOUNCEMENT_VIDEO.videoId} locale={me.locale} />
          )}
          {VIDEO_RELEASED && videoCardVisible(videoGate) && (
            <PinnedAnnouncement
              title={pinned ? pinned.title : ANNOUNCEMENT_VIDEO.title}
              body={pinned?.body}
              postedAgo={pinned ? relativeTime(pinned.publishedAt, me.locale) : undefined}
              embedUrl={ANNOUNCEMENT_VIDEO.embedUrl}
              videoTitle={ANNOUNCEMENT_VIDEO.title}
              countViewsAs={videoGate?.shown ? ANNOUNCEMENT_VIDEO.videoId : undefined}
            />
          )}

          {/* NO "Company feed" HEADER and no post count (David, 12 Oct: "it's
              already obvious"). The card has no title, so Card draws no head. */}
          <Card className="card--feed">
            {content.kind === 'loading' && (
              <p className="empty" aria-busy="true">
                {h.loadingFeed}
              </p>
            )}

            {/* An error here is worth showing rather than swallowing into an
                empty list. RLS denying everything and the network failing look
                identical from an empty array, and the first is the failure this
                project keeps hitting. */}
            {content.kind === 'error' && (
              <p className="empty">
                {h.feedError} <span className="mono">{content.message}</span>
              </p>
            )}

            {/* THE POST BAR IS GONE (David, 12 Oct): "the new pencil icon is
                great and is all that's needed." Post something, Recognize a
                colleague and Your drafts are all in PostFab's menu, the
                pencil at the bottom right, on every width and at every depth
                of the feed — one door onto the one composer (FED-35, 059).
                Recognize keeps its second door on the rail's Star One card
                (30 Sep). Do not put a row of buttons back above the feed. */}

            {/* Outside the dialog, deliberately: the dialog closes on a
                successful post, so a notice rendered inside it would be
                destroyed by the event it is reporting. B09's fail-open means
                the post IS live and unscreened, and the author finding that
                out from the feed is what the composer's own header says must
                not happen. */}
            {unchecked && (
              <p className="composer__unchecked" role="status">
                {h.postedUnchecked}
              </p>
            )}
            {editLoadFailed && (
              <p className="composer__unchecked" role="alert">
                {h.editLoadFailed}
              </p>
            )}

            {/* ── BOTH FILTER ROWS ARE GONE. David, 20 Sep. FED-37. ──────────

                There were two, stacked. A one-of-three chip row (All /
                Recognition I received / Recognition I gave) and, under it, the
                audience chips built from `content_audience` org units.

                The recognition chips came from D12: the feed is posts only, so
                those two read `recognition_wall` instead and swapped the whole
                list out. They were answering "where are my recognitions" on the
                screen least suited to it — the rail has two cards for exactly
                that, headed with the two verbs, and #/stars is the screen.

                The audience chips are FED-12's known hole arriving in the UI:
                they filter by org unit because there IS no topic taxonomy, so a
                reader looking for "things about my site" gets a list of legal
                entities. FED-37 is the issue for what replaces them; the
                honest interim is nothing rather than a control that does not
                answer the question it looks like it answers.

                `filterByAudience` and `shapeFeed`'s `counts` are KEPT in
                lib/feed.ts with their tests. They are correct, they are the
                only arithmetic on this page that can be wrong while still
                compiling, and FED-37 will want them. */}

            {content.kind === 'ready' &&
              (visible.length === 0 ? (
                  /* One message now, because there is no filter to clear.
                     "Nothing for this audience" was only ever true while the
                     audience chips existed. */
                  <p className="empty">{h.nothingPublished}</p>
                ) : (
                  <div className="feed">
                    {visible.map((p, i) => {
                      const gap = placement.gapAt[i]
                      const group = gap === null || gap === undefined ? undefined : recGroups.get(gap)
                      return (
                      <Fragment key={p.contentId}>
                      {group && (
                        <FeedRecognitions group={group} onChanged={() => setRecVersion((v) => v + 1)} />
                      )}
                      <FeedPost
                        post={p}
                        locale={me.locale}
                        extra={<ContestResults contentId={p.contentId} />}
                        /* A FUNCTION, called only when the reader opens the
                           thread — so thirty posts make no comments_for()
                           calls until one is asked for, and the card can say
                           whether they came to read or to write. See
                           FeedPost's `thread` prop. */
                        onEdit={
                          /* 300: or a byline post the reader holds the byline
                             of (decision 3). The server decides either way. */
                          canPost && (p.authorPersonId === me.personId || p.byline?.iHold === true) && !p.hiddenAt
                            ? () => void openLiveEdit(p.contentId)
                            : undefined
                        }
                        thread={({ autoFocus }) => (
                          <Thread
                            contentId={p.contentId}
                            me={me}
                            labels={POST_LABELS}
                            autoFocus={autoFocus}
                            reviewing={reviewing}
                          />
                        )}
                        moderationBar={
                          moderatorMode && canModerate ? (
                            <PostModerationBar
                              post={p}
                              checkNote={checkNotes ? (checkNotes.get(p.contentId) ?? null) : undefined}
                              // Re-read, never splice. After a remove the
                              // moderator cannot see the row at all
                              // (invariant 16c), so local edits would be
                              // guessing at a state they cannot observe.
                              onDone={() => setFeedVersion((v) => v + 1)}
                            />
                          ) : undefined
                        }
                      />
                      </Fragment>
                      )
                    })}
                  </div>
                ))}
          </Card>

          {/* Role administration used to live here, as a card below the feed,
              because the app had no router. It now has one and this moved to
              the Administration section — see routes/Admin.tsx for why that
              matters more at ~900 people than it did at four. */}
        </div>

        {/* THE RAIL IS HOOKS (David, 11 Oct). Every card here is short and
            fixed: the visual, one line, one action, with the detail behind a
            dialog or the card's own screen. That is what lets the same rail
            become ONE SWIPE ROW above the feed below 1100px (`.hub__hooks`
            in hub.css) instead of a column nobody reaches under an endless
            feed — Facebook's reels strip, not a second page.

            One Question and the contest come first: they are the reason to
            come back this week, and on a phone they are the first two tiles. */}
        <aside className="hub__side hub__hooks" aria-label={h.hooksLabel}>
          <OneQuestionCard
            hub={oneQuestion}
            locale={me.locale}
            onChanged={() => setOneQuestionVersion((v) => v + 1)}
          />
          {contest && <ContestCard contest={contest} />}
          {/* "Your unpublished posts" used to sit above this, appearing only
              when you had something unpublished. It is now the "Your drafts"
              button in the feed, and the move is the point: a surface that is
              invisible until you already have a draft is a surface nobody
              looks for. SHL-16, FED-35, and DraftPicker's own header.

              The rebrand rollout board sat below, the only surface CMP-2 ever
              had. David retired it on 20 Sep — the widget, the two functions
              and the 230-row asset registry behind them, which had a photo
              table with no storage bucket in front of it and zero rows in it
              since 30 August. Migration 094 part D. It is a RETIREMENT and not
              a tidy-up, which is why CMP-2 says so rather than going quiet. */}
          <CountdownCard countdown={countdown} locale={me.locale} />
          {/* REC-8. Below trending and above Wiki One: thanking a colleague is
              something you choose to do, which puts it with the conversation
              rather than with the campaign above or the reference below. */}
          {/* Two cards, two currencies, two verbs — see StarOneWidget's header.
              Give sits above spend because the allowance EXPIRES and the
              balance does not, so the one with a deadline is the one to meet
              first. */}
          {/* 165 (David, 11 Oct): before somebody joins Star One, the two
              cards are ONE agreement card — no balance, no allowance, no
              recent stars. 'unknown' (my_terms not read) draws neither. */}
          {starOne.status === 'agreed' ? (
            <>
              <StarsGivenWidget stars={stars} onGive={openGive} />
              <StarsReceivedWidget stars={stars} series={starSeries} locale={me.locale} />
            </>
          ) : starOne.status === 'needed' ? (
            <StarOneAgreement variant="card" />
          ) : null}
          <WikiAnswers
            answers={content.kind === 'ready' ? content.wiki : []}
            locale={me.locale}
          />

          {/* THE BUSINESS CARD (David, 4 Oct). Below the reference and above
              trending, which stays last (20 Sep). A tool you reach for when you
              meet somebody, not an obligation — so it sits with Wiki One, the
              other thing you go to rather than something owed. It opens the
              shell's one card dialog, the same one the identity menu opens,
              and renders nothing while the card is held. */}
          <MyCardWidget name={me.displayName} />

          {/* LAST IN THE RAIL. David, 20 Sep — it used to sit second, under the
              countdown and above the star cards.

              The order down this column is how much of it is anybody's
              obligation: the company's one shared date, then what you owe a
              colleague, then where to go with a question. Trending tags are
              none of those — they are the most browsable thing here and the
              least owed, so they go at the foot where browsing belongs.

              It also renders NOTHING when there are no tags (its catch sets an
              empty list), and a card that sometimes is not there is better at
              the bottom than punching a hole in the middle of the column. */}
          <TrendingTags tags={trending} />

          {/* THE "CONNECTION FACTS" CARD WAS HERE AND IS GONE. David, 20 Sep.

              It printed the session → identity → person → tenant → role chain
              with the person's own id, email and roles behind a Show button,
              hidden in employee view. It was build-time diagnostics from the
              days when that chain broke weekly, and it earned its place then.

              It does not now: the chain is proven, and the card put a person's
              uuid and verification state on the hub's first screen for every
              one of ~900 people who happens to hold a second role. Do not put
              it back on the hub. If the chain needs interrogating again it
              belongs behind #/admin, where the audience is the build team
              rather than the company. */}

        </aside>
      </div>

      {/* ── THE TWO DIALOGS, MOUNTED ONCE, BY THE ROUTE ──────────────────────
          FED-35's instruction and 059's rule: one composer on this page, so
          "Post something" means the same thing everywhere. Putting either of
          these inside the feed card would make a second one the moment
          anything else wants to open a composer.

          <Dialog> is the app's only real modal (REC-20) — it traps Tab, locks
          the page behind it, returns focus on the way out, and its scrim
          closes it only while nothing has been typed (David, 29 Sep), so a
          stray click still cannot destroy a half-written post. */}
      {/* The ONLY way to post from the hub since 12 Oct — the bar above the
          feed is gone (David). Outside .hub__cols on purpose: the
          rail is sticky, a sticky element forms a stacking context, and a
          fixed button inside one sits under the topbar (SHL-30's lesson). */}
      <PostFab
        onRecognize={openGive}
        onPost={canPost ? startPost : undefined}
        onDrafts={canPost ? () => setPickingDraft(true) : undefined}
      />

      {canPost && posting && (
        <Dialog
          open
          title={editingLive ? h.editPost : editing ? h.editDraft : h.postSomething}
          className="composerdialog"
          onClose={() => {
            setPosting(false)
            setEditing(null)
            setEditingLive(false)
          }}
        >
          <Composer
            authorName={me.displayName}
            /* The storage path is `<tenant>/<person>/<uuid>.jpg` and the 019
               Storage policies read both segments, so the composer needs them
               before it can upload anything. */
            tenantId={me.tenants[0]?.id ?? null}
            personId={me.personId}
            draft={editing}
            live={editingLive}
            bylines={myBylines}
            onPosted={(_id, wasUnchecked) => {
              setUnchecked(wasUnchecked)
              setFeedVersion((v) => v + 1)
            }}
            onClose={() => {
              setPosting(false)
              setEditing(null)
              setEditingLive(false)
            }}
          />
        </Dialog>
      )}

      {/* THE SAME GIVE FORM STAR ONE OPENS, in the same Dialog (REC-20). One
          form, three doors (the rail card, the top of the feed, and #/stars) —
          never a copy; see StarOneWidget's header. */}
      <Dialog open={giving} title={STARS[lang].recognizeColleague} onClose={() => setGiving(false)}>
        {giveFeatured === null ? (
          <p className="empty" aria-busy="true">…</p>
        ) : (
          <GiveForm
            featured={giveFeatured}
            allowanceRemaining={stars?.allowanceRemaining ?? 0}
            onGiven={afterGive}
            onDone={() => setGiving(false)}
          />
        )}
      </Dialog>

      {canPost && pickingDraft && (
        <Dialog open title={h.yourDrafts} onClose={() => setPickingDraft(false)}>
          <DraftPicker
            onPick={(draft) => {
              /* The picker hands the draft up and the POST dialog opens on it.
                 Two dialogs are never open at once — the picker closes in the
                 same commit — because Dialog locks body scroll and two of them
                 would each restore a different previous value on the way out. */
              setPickingDraft(false)
              setEditing(draft)
              setEditingLive(false)
              setPosting(true)
            }}
            onClose={() => setPickingDraft(false)}
          />
        </Dialog>
      )}
    </AppShell>
  )
}

/**
 * Display initials from a display name. First and last token, so "Tomás
 * Herrera" gives TH and a single-token name gives one letter. Array.from
 * rather than [0] so a name starting with a character outside the BMP is not
 * cut in half — display_name is free text from HR, not ASCII.
 */
function initialsOf(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = Array.from(parts[0])[0] ?? ''
  const last = parts.length > 1 ? (Array.from(parts[parts.length - 1])[0] ?? '') : ''
  return (first + last).toUpperCase()
}
